<script setup>
    import MainHeader from '@/components/HeaderComponent.vue';
    import SideMenu from '@/components/ManagementSideMenuComponent.vue';
    import { ref, onMounted, inject, getCurrentInstance } from 'vue';
    import { useManagementStore } from '@/stores/management/managementStore';
    import { useFileDownloadStore } from '@/stores/common/fileDownloadStore';
    import { useFileUploadStore } from '@/stores/common/fileUploadStore';
    import fileUploadDownloadService from '@/commonProject/service/fileUploadDownloadService';
    import detail from '@/components/modal/ManagementLocationDetailComponent.vue';
    import deleteModal from '@/components/modal/DeleteConfirmComponent.vue';
    import Utils from '@/commonProject/helpers/utils.js';
    import Pagination from '@/components/PaginationComponent.vue';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementStore = useManagementStore();
    // ファイルダウンロードストア
    const fileDownloadStore = useFileDownloadStore();
    // ファイルアップロードストア
    const fileUploadStore = useFileUploadStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // ページングパーツ
    const currentPage = ref(1);
    const totalPages = ref(0);

    // 削除用PK保持
    const deletedPk = ref('');

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();
    /**
     * 初期処理
     */
    onMounted(async () => {
        // 一覧取得
        await getList();
    });

    /**
     * 一覧取得処理
     */
    async function getList() {
        const maxPage = 50;
        const requestBody = {
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        const response = await managementStore.postLocLstAction(API_URL, requestBody);
        if (response.success) {
            totalPages.value = response.RowResult;
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * ページ操作
     * @param newPage
     */
    async function handlePageChange(newPage) {
        currentPage.value = newPage;

        // ページング実行により、一覧取得APIを再実行する。
        await getList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    /**
     * CSVダウンロード処理
     */
    async function downloadUCsv() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const response = await managementStore.postLocDldAction(API_URL, {});
        if (response.success) {
            /*--- ダウンロード処理(修正前) - ここから ---*/
            /*
            const DldHistPk = response.responseBody.DldHistPk;
            if (!Utils.isNullEmpty(DldHistPk)) {
                // クエリパラメーターを作成する。
                const queryParam = {
                    DldHistPk: DldHistPk, //ダウンロード履歴PK
                };

                let result = null;
                downloadCheck: do {
                    // ダウンロード履歴取得APIをコールする。
                    const response2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParam);
                    if (!response2.success) {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }

                    result = await fileUploadDownloadService.fileDownload(response2.responseBody.DldStatus, response2.responseBody.DldS3Url, 'location.csv');
                    switchByResult: switch (result) {
                        case 'startDownload':
                            {
                                const requestBody2 = {
                                    DldHistPk: DldHistPk,
                                    DldStatus: '3',
                                };
                                const response3 = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBody2);
                                if (!response3.success) {
                                    messageDialog('error', response3.errorMessage);
                                    if (response3.redirect) {
                                        location.href = response3.redirectUrl;
                                    }
                                }
                                result = 'continue';
                            }
                            break switchByResult;
                        case 'endDownload':
                            {
                                const requestBody2 = {
                                    DldHistPk: DldHistPk,
                                    DldStatus: '4',
                                };
                                const response3 = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBody2);
                                if (!response3.success) {
                                    messageDialog('error', response3.errorMessage);
                                    if (response3.redirect) {
                                        location.href = response3.redirectUrl;
                                    }
                                }
                                result = 'continue';
                            }
                            break switchByResult;
                        case 'error':
                            break downloadCheck;
                        case 'break':
                            break downloadCheck;
                        default:
                            continue downloadCheck;
                    }
                } while (result === 'continue');
            }
            */
            /*--- ダウンロード処理(修正前) - ここまで ---*/
            /*--- ダウンロード処理(修正後) - ここから ---*/
            const DldHistPk = response.responseBody.DldHistPk;
            if (!Utils.isNullEmpty(DldHistPk)) {
                // ダウンロード履歴取得APIのクエリパラメーターを生成する。
                const queryParamDldHistGet = {
                    DldHistPk: DldHistPk, //ダウンロード履歴PK
                };

                // 念のためタイマーイベントを全てクリアする。
                // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                clearTimeout();

                // タイマーで監視し、APIのエラー時のループを制御する。
                let isDownloadTimeoutError = false;

                // ダウンロード処理開始後からダウンロード処理が完了せずに15分経過した場合は、エラーとしてダウンロード処理を停止する。
                const downloadTotalErrorTimer = 900000;

                // ダウンロード履歴取得APIをコールする間隔のタイマー値を設定する。
                // ダウンロード可能かどうかのチェック処理後、2秒間のインターバルを設けて、再度APIをコールする。
                const callDownloadHistoryApiintervalTimer = 2000;

                // ダウンロード対象のファイル名を宣言する。
                const fileName = 'location.csv';

                // ダウンロード処理の総経過時間を監視し、15分経過した場合に呼び出されるファンクションを宣言する。
                const checkTimeout = function () {
                    // ダウンロード総経過時間監視フラグを有効にして、ダウンロード限界時間経過としてエラー発生扱いにする。
                    isDownloadTimeoutError = true;

                    // 念のためタイマーイベントを全てクリアする。
                    // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                    clearTimeout();
                };

                // ダウンロード処理構造体を宣言する。
                // ダウンロード履歴取得API、ダウンロード履歴更新APIの実行タイミングを制御し、ダウンロード可能なファイルが存在する場合はJavascriptでダウンロードを実行する。
                const downloadEventPromise = async function (id) {
                    await new Promise(function (resolve, reject) {
                        // ダウンロード履歴取得APIから、ダウンロード対象ファイルの状態を取得し、本処理で判定して制御する。

                        // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行せず、ダウンロード限界時間経過としてエラーにして、rejectで返却する。
                        if (isDownloadTimeoutError) {
                            reject('download timeout error');
                            return;
                        }

                        // ダウンロード履歴取得APIの判定したダウンロードファイルの状態に応じて制御する。
                        // ファイルダウンロード処理Javascript側で状況を判定している。
                        switch (id) {
                            case 'continue':
                            case 'startDownload':
                            case 'endDownload':
                                // ダウンロード準備中。
                                // ダウンロード可能状態の応答が返ってきた。
                                // Javascriptでファイルをダウンロードして、正常に完了した状態。
                                resolve(id);
                                break;
                            case 'error':
                            case 'break':
                            default:
                                // ダウンロード履歴取得APIでダウンロードエラーが発生。
                                // ダウンロード履歴取得APIからダウンロード完了が返却された場合。
                                // 上記以外の本処理内での例外の場合。
                                // ダウンロード処理を停止するためにrejectで返却する。
                                reject(id);
                                break;
                        }
                    })
                        .then(async function (resolved) {
                            // Promiseでresolveの正常応答の場合。

                            // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行しない。
                            if (isDownloadTimeoutError) {
                                return;
                            }

                            // ダウンロード履歴取得APIから取得したステータスと、ダウンロード処理スクリプトで判定した結果に応じて制御する。
                            switch (resolved) {
                                case 'startDownload':
                                    {
                                        // ダウンロード履歴取得APIからダウンロード可能ステータスが返却されたので、Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '3', // ダウンロード中モードに設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'endDownload':
                                    {
                                        // Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '4', // ダウンロード完了に設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'continue':
                                    // ダウンロード履歴取得APIを指定のインターバルの時間経過後に再度コールするタイマーイベント。
                                    setTimeout(async function () {
                                        // ダウンロード履歴取得APIをコールする。
                                        const responseDldHistGet2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistGet2.success) {
                                            // ファイルダウンロードスクリプトを実行する。
                                            // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                                            const fileUploadDownloadServiceResult2 = await fileUploadDownloadService.fileDownload(responseDldHistGet2.responseBody.DldStatus, responseDldHistGet2.responseBody.DldS3Url, fileName);

                                            // ファイルダウンロードスクリプト実行後、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise(fileUploadDownloadServiceResult2);
                                        } else {
                                            // ダウンロード履歴取得APIがエラーの場合。
                                            messageDialog('error', responseDldHistGet2.errorMessage);
                                            if (responseDldHistGet2.redirect) {
                                                location.href = responseDldHistGet2.redirectUrl;
                                            }
                                        }
                                    }, callDownloadHistoryApiintervalTimer);
                                    break;
                                default:
                                    // 何もしない
                                    break;
                            }
                        })
                        .catch(function (/*rejected*/) {
                            // Promiseでrejectのルートで処理された場合。

                            // ダウンロードが完了した、や、エラーが発生したので、ダウンロード総経過時間フラグを更新して、以降の処理を実行しないようにする。
                            isDownloadTimeoutError = true;

                            // 念のためタイマーイベントを全てクリアする。
                            // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                            clearTimeout();
                        });
                };

                // ***** ダウンロードの初期処理を実行する - ここから - **** //

                // ダウンロード総時間を監視する。
                setTimeout(checkTimeout, downloadTotalErrorTimer);

                // 初回のダウンロード履歴取得APIをコールする。
                const responseDldHistGet = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                // APIの応答結果に応じて制御する。
                if (responseDldHistGet.success) {
                    // ファイルダウンロードスクリプトを実行する。
                    // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                    const fileUploadDownloadServiceResult = await fileUploadDownloadService.fileDownload(responseDldHistGet.responseBody.DldStatus, responseDldHistGet.responseBody.DldS3Url, fileName);

                    // ファイルダウンロードスクリプト実行後、ファイルダウンロード処理Promiseを実行する。
                    await downloadEventPromise(fileUploadDownloadServiceResult);
                } else {
                    // ダウンロード履歴取得APIがエラーの場合。
                    messageDialog('error', responseDldHistGet.errorMessage);
                    if (responseDldHistGet.redirect) {
                        location.href = responseDldHistGet.redirectUrl;
                    }
                }

                // ***** ダウンロードの初期処理を実行する - ここまで - **** //
            }
            /*--- ダウンロード処理(修正後) - ここまで ---*/
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * CSVアップロード処理
     */
    const selectedUploadFile = ref([]);
    async function uploadCsv(e) {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const files = e.target.files || e.dataTransfer.files;
        const uploadTargetFile = {
            name: files[0].name,
            type: files[0].type,
            data: files[0],
        };

        // 選択したファイルを初期化する。
        e.target.value = '';

        // データ格納前に初期化する。
        selectedUploadFile.value = [];

        selectedUploadFile.value.push(uploadTargetFile);
        if (selectedUploadFile.value.length > 0) {
            for (let i = 0; i < selectedUploadFile.value.length; i++) {
                const data = selectedUploadFile.value[i];

                const requestBody = {
                    FileNm: data.name,
                };

                // ファイルアップロードAPIをコールする。
                const response = await managementStore.postLocUldAction(API_URL, requestBody);
                if (response.success) {
                    const queryParam3 = {
                        UldHistPk: response.responseBody.UldHistPk,
                    };
                    // アップロード履歴取得APIをコールする。
                    const response2 = await fileUploadStore.getUploadFileHistoryPkFromApi(API_URL, queryParam3);
                    if (response2.success) {
                        const fileUploadResults = await fileUploadDownloadService.fileUpload(response2.responseBody.UldS3Url, data.data);

                        if (fileUploadResults.status === 200) {
                            const requestBody3 = {
                                UldHistPk: response.responseBody.UldHistPk,
                                UldStatus: '2',
                            };
                            // アップロード履歴更新APIをコールする。
                            const response3 = await fileUploadStore.updateUploadFileHistoryPkFromApi(API_URL, requestBody3);
                            if (!response3.success) {
                                messageDialog('error', response3.errorMessage);
                                if (response3.redirect) {
                                    location.href = response3.redirectUrl;
                                }
                            }
                        }
                    } else {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                }
            }
        }
    }

    /**
     * 削除処理
     * @param LocationPk PK
     */
    async function deleted(LocationPk) {
        // 勤務地削除APIのリクエストボディを生成する。
        const requestBody = {
            LocationPks: [LocationPk],
        };

        // 勤務地削除APIをコールする。
        const response = await managementStore.postLocDelAction(API_URL, requestBody);

        if (response.success) {
            // 1ページ目に座標を戻す。
            currentPage.value = 1;

            // 一覧を取得しなおす。
            await getList();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 詳細モーダル
     * @param LocationPk PK
     */
    const detailDispFlg = ref('');
    const detailComponent = ref();
    function openDetail(LocationPk) {
        detailComponent.value.setInfo(LocationPk);
        detailDispFlg.value = 'block';
    }

    function closeDetail() {
        detailDispFlg.value = '';
    }

    async function closeDetailAndUpdateList() {
        detailDispFlg.value = '';

        // 1ページ目に座標を戻す。
        currentPage.value = 1;

        // 一覧を取得しなおす。
        await getList();
    }

    /**
     * 削除確認モーダル
     * @param LocationPk
     */
    const deletedDispFlg = ref('');
    async function deletedModal(LocationPk, TouDataFlag) {
        if (TouDataFlag) {
            messageDialog('error', i18n.global.t('ManagementLocationList.message.message1'));
            return;
        }
        if (!Utils.isNullEmpty(LocationPk)) {
            deletedPk.value = LocationPk;
            deletedDispFlg.value = 'block';
        }
    }

    /**
     * 削除確認モーダル「いいえ」
     */
    async function backModal() {
        deletedPk.value = '';
        deletedDispFlg.value = '';
    }

    /**
     * 削除確認モーダル「はい」
     */
    async function deletedOk() {
        await deleted(deletedPk.value);
        deletedPk.value = '';
        deletedDispFlg.value = '';
    }
</script>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: detailDispFlg }">
            <detail ref="detailComponent" @detailModal:cancel="closeDetail" @detailModal:closeAndUpdate="closeDetailAndUpdateList" />
        </section>
        <!--詳細確認モーダル-->
        <section id="overlay" :style="{ display: deletedDispFlg }">
            <deleteModal ref="deletedComponent" @deletedModal:close="backModal" @deletedModal:delete="deletedOk" />
        </section>
        <!--削除確認モーダル-->
        <MainHeader />
        <div id="contents" class="sideMenu headerSingleBlock">
            <SideMenu />
            <div id="rightBlock" class="noPaddingTop">
                <div class="whiteBox wide">
                    <h3 class="textCenter">
                        <span>{{ $t('ManagementLocationList.parts.pageTitle1') }} </span>
                    </h3>
                    <section class="whiteBoxContents">
                        <ul class="btnArea flexLeft" style="margin-bottom: 10px">
                            <li style="margin-right: 10px">
                                <button class="btnPrimary addRowDataBtn" @click="openDetail()">{{ $t('ManagementLocationList.parts.button1') }}</button>
                            </li>
                            <li>
                                <label class="btnPrimary button" :class="{ disabled: disabledStore.isDisabled }">
                                    <span><input type="file" id="file" @change="uploadCsv($event)" :disabled="disabledStore.isDisabled" style="display: none" />{{ $t('ManagementLocationList.parts.button2') }} </span>
                                </label>
                            </li>
                            <li style="margin-left: auto">
                                <button class="btnNormal" @click="downloadUCsv" :disabled="disabledStore.isDisabled">{{ $t('ManagementLocationList.parts.button3') }}</button>
                            </li>
                        </ul>
                        <article class="innerTable" style="margin-top: 20px">
                            <table class="table noSelection" id="sample">
                                <thead>
                                    <tr>
                                        <th style="width: 150px">
                                            <span>{{ $t('ManagementLocationList.parts.dl1') }} </span>
                                        </th>
                                        <th style="width: auto">
                                            <span>{{ $t('ManagementLocationList.parts.dl2') }} </span>
                                        </th>
                                        <th style="width: 30px"><span>編集</span></th>
                                        <th style="width: 30px"><span>削除</span></th>
                                    </tr>
                                </thead>
                                <tbody v-for="contents in managementStore.locationList" :key="contents.index">
                                    <tr>
                                        <td>
                                            <span>{{ contents.LocationId }}</span>
                                        </td>
                                        <td>
                                            <span>{{ contents.LocationNm }}</span>
                                        </td>
                                        <td>
                                            <button class="btnEdit editRowDataBtn" @click="openDetail(contents.LocationPk)">{{ $t('ManagementLocationList.parts.button4') }}</button>
                                        </td>
                                        <td>
                                            <button class="btnDelete" @click="deletedModal(contents.LocationPk, contents.TouDataFlag)">{{ $t('ManagementLocationList.parts.button5') }}</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                        <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                    </section>
                    <section class="whiteBoxFooter">
                        <ul class="btnArea center">
                            <RouterLink :to="{ name: 'home' }">
                                <button type="button" id="btnBack" class="btnNormal">{{ $t('ManagementLocationList.parts.button6') }}</button>
                            </RouterLink>
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    </section>
</template>

<style></style>
